import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CajaListTable, { getCajaListTableColumns } from 'components/tables/CajaListTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import axios, { AxiosError } from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { MovimientoDeCaja } from 'data/project-management/movimientoDeCaja';
import { Link } from 'react-router-dom';
import { useAuth } from 'providers/AuthProvider';
import { Form, Spinner } from 'react-bootstrap';

interface Gasto { gastoId: string; gasto: string, concepto: string; }
interface UsuarioCaja { id: number; usuario: string; caja: string; }
interface SaldoCaja { [key: string]: number; }
interface SaldosResponse { total: number; porCaja: SaldoCaja; fecha: string; }

const CajaListView = () => {
  const [caja, setCaja] = useState<MovimientoDeCaja[]>([]);
  const [categorias, setCategorias] = useState<Gasto[]>([]);
  const [uniqueConceptos, setUniqueConceptos] = useState<string[]>([]);
  const [availableCajas, setAvailableCajas] = useState<string[]>([]);
  const [selectedCaja, setSelectedCaja] = useState<string>('');
  const [saldo, setSaldo] = useState<number | null>(null);
  const [loadingSaldo, setLoadingSaldo] = useState<boolean>(false);
  const [saldosTotales, setSaldosTotales] = useState<SaldosResponse | null>(null);
  const [loadingSaldos, setLoadingSaldos] = useState<boolean>(false);
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';
  const today = new Date();
  const [month, setMonth] = useState<string>(String(today.getMonth() + 1).padStart(2, '0'));
  const [year, setYear] = useState<string>(String(today.getFullYear()));
  const [category, setCategory] = useState<string>('');

  useEffect(() => {
    if (user?.nivel_acceso === 'socio') {
      const fetchSaldos = async () => {
        setLoadingSaldos(true);
        try {
          const res = await axios.get<SaldosResponse>(`${apiUrl}/caja/saldos`);
          setSaldosTotales(res.data);
        } catch (err) {
          console.error('Error fetching saldos totales:', err);
          setSaldosTotales(null);
        } finally {
          setLoadingSaldos(false);
        }
      };
      fetchSaldos();
    }
  }, [user?.nivel_acceso, apiUrl]);

  // Fetch available cajas + add caja_general for socio
  useEffect(() => {
    if (user?.iniciales && user.nivel_acceso !== 'usuario') {
      const fetchAvailableCajas = async () => {
        try {
          const response = await axios.get<UsuarioCaja[]>(
            `${apiUrl}/usuario_cajas_m2m/usuario/${user.iniciales}`
          );
          let cajas = response.data.map(item => item.caja);

          // Always include caja_general for socio
          if (user.nivel_acceso === 'socio' && !cajas.includes('caja_general')) {
            cajas = ['caja_general', ...cajas]; // Put GENERAL first
          }

          setAvailableCajas(cajas);

          // CORRECT: always use 'caja_general' string
          if (user.nivel_acceso === 'socio') {
            setSelectedCaja('general');
          } else if (cajas.includes(user.iniciales)) {
            setSelectedCaja(user.iniciales);
          } else {
            setSelectedCaja(cajas[0] || user.iniciales);
          }
        } catch (error) {
          console.error('Error fetching available cajas:', error);
          if (user.nivel_acceso === 'socio') {
            setAvailableCajas(['caja_general']);
            setSelectedCaja('general');
          } else {
            setSelectedCaja(user.iniciales);
          }
        }
      };
      fetchAvailableCajas();
    } else if (user?.iniciales) {
      setSelectedCaja(user.iniciales);
    }
  }, [user?.iniciales, user?.nivel_acceso, apiUrl]);

  // Fetch movimientos
  useEffect(() => {
    const fetchCaja = async () => {
      if (!selectedCaja) {
        setCaja([]);
        return;
      }

      try {
        let url = `${apiUrl}/caja/${selectedCaja}`;

        // CASO ESPECIAL: caja_general tiene su propia ruta
        if (selectedCaja === 'caja_general') {
          url = `${apiUrl}/caja_general`;  // ← ¡¡AQUÍ ESTÁ LA CLAVE!!
        }

        const response = await axios.get<MovimientoDeCaja[]>(url);
        const data = Array.isArray(response.data) ? response.data : [];
        console.log(`Movimientos de ${selectedCaja}:`, data);
        setCaja(data);
      } catch (error) {
      const err = error as AxiosError;
      console.error(
      'Error fetching movimientos:',
      err.response?.status,
      err.message
    );
    setCaja([]);
  }
    };
    fetchCaja();
  }, [selectedCaja, apiUrl]);

  // Fetch saldo ONLY if NOT caja_general
  useEffect(() => {
    if (!selectedCaja || selectedCaja === 'caja_general') {
      setSaldo(null);
      setLoadingSaldo(false);
      return;
    }

    const fetchSaldo = async () => {
      setLoadingSaldo(true);
      try {
        const response = await axios.get<{ saldo: number }>(
          `${apiUrl}/caja/saldo/${selectedCaja}`
        );
        setSaldo(response.data.saldo);
      } catch (error) {
        console.error('Error fetching saldo:', error);
        setSaldo(null);
      } finally {
        setLoadingSaldo(false);
      }
    };

    fetchSaldo();
  }, [selectedCaja, apiUrl]);

  // Fetch categorias
  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        const response = await axios.get<Gasto[]>(`${apiUrl}/gastos/`);
        setCategorias(response.data);

        const unique = Array.from(new Set(response.data.map(item => item.concepto)))
        .filter(c => c) // Remove null/undefined
        .sort();        // Optional: Sort them alphabetically
      
      setUniqueConceptos(unique);
      } catch (error) {
        console.error('Error fetching gastos: ', error);
      }
    };
    fetchCategorias();
  }, [apiUrl]);

  // Safe filtered data
  const filteredCaja = useMemo(() => {
    const safeCaja = Array.isArray(caja) ? caja : [];
    return safeCaja
      .filter((movimiento) => {
        const createdDate = new Date(movimiento.created_at);
        const yearMatch = year ? createdDate.getFullYear() === parseInt(year) : true;
        const monthMatch = month ? createdDate.getMonth() + 1 === parseInt(month) : true;
        const categoryMatch = category ? movimiento.categoria === category : true;
        return yearMatch && monthMatch && categoryMatch;
      })
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [caja, month, year, category]);

  const months = [
    { value: '', label: 'Todos los meses' },
    { value: '01', label: 'Enero' }, { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' }, { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' }, { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' }, { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' }, { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' }, { value: '12', label: 'Diciembre' }
  ];

  const years = [
    { value: '', label: 'Todos los años' },
    { value: '2026', label: '2026' },
    { value: '2025', label: '2025' },
    { value: '2024', label: '2024' },
    { value: '2023', label: '2023' }
  ];

  const categoryTotals = useMemo(() => {
    return filteredCaja.reduce(
      (acc, mov) => {
        const monto = Math.abs(Number(mov.monto) || 0);
        const cat = mov.categoria || 'Sin Categoría';

        if (mov.cobros_fk !== null) {
          acc.totalCobros += monto;
          acc.cobrosByCat[cat] = (acc.cobrosByCat[cat] || 0) + monto;
        } else if (mov.pagos_fk !== null) {
          acc.totalPagos += monto;
          acc.pagosByCat[cat] = (acc.pagosByCat[cat] || 0) + monto;
        }
        return acc;
      },
      { 
        totalCobros: 0, 
        totalPagos: 0, 
        cobrosByCat: {} as Record<string, number>, 
        pagosByCat: {} as Record<string, number> 
      }
    );
  }, [filteredCaja]);
  
  const isGeneral = selectedCaja === 'caja_general' || selectedCaja === 'general';

  const dynamicColumns = useMemo(() => getCajaListTableColumns(isGeneral), [isGeneral]);

  const table = useAdvanceTable<MovimientoDeCaja>({
    data: filteredCaja,
    columns: dynamicColumns,
    pageSize: 2400,
    pagination: true,
    sortable: true
  });

  const formatCajaName = (table: string) => {
    return table.replace('caja_', '').toUpperCase().replace('_', ' ');
  };

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-start justify-content-between">
          <h2 className="mb-0 d-flex flex-column align-items-start">
        <div className="d-flex align-items-center">
              {user?.nivel_acceso !== 'usuario' && availableCajas.length > 0 ? (
                <>
                  <span className="me-3">Caja</span>
                  <Form.Select
                    value={selectedCaja}
                    onChange={(e) => setSelectedCaja(e.target.value)}
                    className="d-inline-block w-auto me-3"
                    style={{ minWidth: '150px' }}
                  >
                    {availableCajas.map((c) => (
                      <option key={c} value={c}>
                        {c === 'caja_general' ? 'GENERAL' : formatCajaName(c)}
                      </option>
                    ))}
                  </Form.Select>
                </>
              ) : (
                <span className="me-3">Caja {user?.iniciales}</span>
              )}
        </div>
        <div className="mt-1">
              {selectedCaja === 'general' ? (
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Saldo: <strong className="text-primary">Ver en panel de totales</strong>
                </span>
              ) : loadingSaldo ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Saldo Actual: <strong className="text-success">
                    ${saldo !== null ? saldo.toLocaleString() : '—'}
                  </strong>
                </span>
              )}
        </div>
        </h2>
        <div>
          <Link className="btn btn-primary px-5 me-2" to="/app/apps/pagos/pagos-generales-list-view" >
            Pagos Pendientes
          </Link>

          <Link className="btn btn-primary px-5" to="/app/apps/pagos/createpago">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Agregar Nuevo Pago
          </Link>
          </div>
        </div>

        {/* SOCIO: Total Balances Panel */}
        {user?.nivel_acceso === 'socio' && (
          <div className="mb-5" style={{ marginLeft: 'auto', marginRight: '2rem', maxWidth: '380px' }}>
            <div className="text-end mb-3">
              <strong className="text-primary fs-5">Saldos Totales</strong>
            </div>

            {loadingSaldos ? (
              <div className="text-end text-muted small">
                <Spinner animation="border" size="sm" className="me-2" />
                Cargando saldos...
              </div>
            ) : saldosTotales ? (
              <>
                <table className="table table-sm table-borderless" style={{ fontSize: '0.95rem' }}>
                  <tbody>
                    {Object.entries(saldosTotales.porCaja)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([table, amount]) => {
                        const label = table === 'caja_general' ? 'GENERAL' : table.replace('caja_', '').toUpperCase();
                        const value = Number(amount).toLocaleString('es-AR');
                        return (
                          <tr key={table}>
                            <td className="text-muted">{label}</td>
                            <td className="text-end text-muted">{value}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>

                <hr className="my-2" style={{ borderTop: '2px solid #0d6efd' }} />

                <div className="d-flex justify-content-between">
                  <strong className="text-primary">Total</strong>
                  <strong className="text-success fs-7">
                    {saldosTotales.total.toLocaleString('es-AR')}
                  </strong>
                </div>

                <div className="text-end text-muted small mt-2">
                  Actualizado {new Date(saldosTotales.fecha).toLocaleTimeString('es-AR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </>
            ) : (
              <div className="text-end text-danger small">No se pudieron cargar los saldos</div>
            )}
          </div>
        )}

        <div className="mb-4">
          <Form>
            <div className="d-flex flex-wrap gap-3">
              <Form.Group>
                <Form.Label>Mes</Form.Label>
                <Form.Select value={month} onChange={(e) => setMonth(e.target.value)}>
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Año</Form.Label>
                <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y) => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              {['socio', 'pagos', 'supervisor'].includes(user?.nivel_acceso || '') && (
                <Form.Group>
                  <Form.Label>Concepto</Form.Label>
                  <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">Todas las categorías</option>
                    {uniqueConceptos.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
            </div>
          </Form>
        </div>

        {filteredCaja.length === 0 ? (
          <div className="text-center py-5 text-muted">
            {selectedCaja === 'caja_general' ? (
              <>
                <p>No hay movimientos registrados en <strong>Caja General</strong> aún.</p>
                <small>Los movimientos se registran en las cajas individuales.</small>
              </>
            ) : (
              <p>No hay movimientos para este período</p>
            )}
          </div>
        ) : null}

        {/* SOCIO: Statistics Breakdown */}
        {user?.nivel_acceso === 'socio' && filteredCaja.length > 0 && (
          <div className="mb-5 ms-auto me-4" style={{ maxWidth: '450px' }}>
            <h4 className="text-primary border-bottom pb-2 mb-3">Resumen de Categorías</h4>

            {/* INFLOWS BREAKDOWN */}
            <div className="mb-4">
              <h6 className="text-success fw-bold">INGRESOS (Cobros)</h6>
              <table className="table table-sm table-borderless small">
                <tbody>
                  {Object.entries(categoryTotals.cobrosByCat).map(([name, value]) => (
                    <tr key={name}>
                      <td>{name}</td>
                      <td className="text-end text-light">${value.toLocaleString('es-AR')}</td>
                    </tr>
                  ))}
                  <tr className="border-top fw-bold">
                    <td>Subtotal Cobros</td>
                    <td className="text-end text-success">${categoryTotals.totalCobros.toLocaleString('es-AR')}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* OUTFLOWS BREAKDOWN */}
            <div className="mb-4">
              <h6 className="text-danger fw-bold">EGRESOS (Pagos)</h6>
              <table className="table table-sm table-borderless small">
                <tbody>
                  {Object.entries(categoryTotals.pagosByCat)
                    .sort((a, b) => b[1] - a[1]) // Sort by highest spending
                    .map(([name, value]) => (
                      <tr key={name}>
                        <td>{name}</td>
                        <td className="text-end text-light">${value.toLocaleString('es-AR')}</td>
                      </tr>
                    ))}
                  <tr className="border-top fw-bold">
                    <td>Subtotal Pagos</td>
                    <td className="text-end text-danger">${categoryTotals.totalPagos.toLocaleString('es-AR')}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* FINAL BALANCE */}
            <div >
              <span><strong className="fs-6">SALDO:</strong></span>
              <span> <strong className={`fs-6 ${categoryTotals.totalCobros - categoryTotals.totalPagos >= 0 ? 'text-primary' : 'text-danger'}`}>
                ${(categoryTotals.totalCobros - categoryTotals.totalPagos).toLocaleString('es-AR')}</strong></span>
              
            </div>
          </div>
        )}

        <CajaListTable />
      </AdvanceTableProvider>
    </div>
  );
};

export default CajaListView;  